import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { CHAT_MOBILE_COLORS } from '@/constants/residents/mobile/chatColors';
import { FileUploadSection, UploadFile } from '@/components/common/admin/mobile/FileUploadSection';
import { useCreateConversationMutation } from '@/store/api/admin/messagesApi';
import { useAppSelector } from '@/store';
import KeyboardSafeView from '@/components/common/KeyboardSafeView';
import { buildMessageFormData } from '@/services/residents/messages/formData';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { ChatEmptyState } from '@/components/residents/chat/mobile';
import { useChatAttachments } from '@/hooks/residents/useChatAttachments';
import { showPhotoPickerActionSheet } from '@/utils/common/alert';
import AttachmentPreview from '@/components/residents/propertyChat/mobile/AttachmentPreview';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;

type Props = {
  onClose: () => void;
  onCreated?: (conversationId: number, subject?: string) => void;
  initialSubject?: string;
};

export const ResidentCreateConversation: React.FC<Props> = ({ onClose, onCreated, initialSubject }) => {
  const [subject, setSubject] = useState(initialSubject || '');
  const [messageContent, setMessageContent] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  // files (web desktop) retained for web UI
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [createConversation, { isLoading }] = useCreateConversationMutation();
  const propertyId = useAppSelector((s) => s.auth.user?.propertyId);
  const { layoutMode } = useResponsive();
  const insets = useSafeAreaInsets();
  const isWeb = Platform.OS === 'web';
  const isDesktopLayout = layoutMode === 'desktop';
  
  // Mobile attachments using the chat attachments hook
  const { attachments, addFromCamera, addFromLibrary, removeAttachment, resetAttachments } = useChatAttachments();

  const disableSend = useMemo(() => {
    if (isLoading) return true;
    // Both desktop and mobile: require subject AND message body
    return !subject.trim() || !messageContent.trim();
  }, [isLoading, subject, messageContent]);

  const handleSubmit = async () => {
    if (!propertyId) return;
    if (disableSend) return;
    // For create conversation, backend expects 'messageContent' (not 'content')
    // Use web files for desktop, mobile attachments for native mobile
    const filesToUpload = isWeb ? files : attachments;
    const formData = buildMessageFormData({
      isAdmin: false,
      files: filesToUpload as any,
      extras: {
        subject: subject.trim(),
        messageContent: messageContent.trim(),
        sendEmail: String(sendEmail),
        messageType: 'groupChat',
        propertyId: String(propertyId),
      },
    });
    try {
      const resp = await createConversation(formData).unwrap();
      if (resp?.success && resp.response?.id) {
        resetAttachments();
        if (onCreated) {
          onCreated(resp.response.id, subject.trim());
        } else {
          onClose();
        }
      } else if (resp?.success) {
        resetAttachments();
        onClose();
      }
    } catch {}
  };

  if (isDesktopLayout) {
    // Desktop: return desktop layout (responsive to viewport size)
    return (
      <View style={styles.container}>
        <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
          <Text style={styles.title}>New conversation</Text>
          <View style={styles.fieldSection}>
            <Text style={styles.label}>Subject</Text>
            <TextInput
              style={styles.input}
              value={subject}
              onChangeText={setSubject}
              placeholder="Enter message subject"
              placeholderTextColor={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY}
              maxLength={100}
            />
          </View>
          <FileUploadSection
            title="Attachments"
            subtitle="Add files to your message (optional)"
            files={files}
            onFilesChange={setFiles}
            maxFiles={5}
          />
          <View style={styles.compose}>
            <Text style={styles.composeTitle}>{subject || 'Untitled message'}</Text>
            <TextInput
              style={styles.composeInput}
              value={messageContent}
              onChangeText={setMessageContent}
              placeholder="Your message..."
              placeholderTextColor={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY}
              multiline
            />
          </View>
        </ScrollView>
        <View style={styles.footer}>
          <TouchableOpacity style={styles.cancel} onPress={onClose}>
            <Text style={styles.cancelText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.send, disableSend && styles.sendDisabled]}
            onPress={handleSubmit}
            disabled={disableSend}
          >
            <Text style={styles.sendText}>Start Chat</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Mobile layout matching Figma design
  // Use different SVG icons based on button state (icons have background baked in)
  const SendIconActive = RESIDENTS_SVG_COMPONENTS.CHAT_SEND_ACTIVE_BUTTON;
  const SendIconInactive = RESIDENTS_SVG_COMPONENTS.CHAT_SEND_INACTIVE_BUTTON;
  const AttachIcon = RESIDENTS_SVG_COMPONENTS.PAPERCLIP;

  return (
    <KeyboardSafeView isModal>
      <View style={mobileStyles.container}>
        {/* Close button - absolutely positioned in top right of modal */}
        <TouchableOpacity
          style={[mobileStyles.closeButton, { top: Platform.OS === 'android' ? insets.top + 16 : 16 }]}
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <CloseIcon width={16} height={16} color={CHAT_MOBILE_COLORS.textMuted} />
        </TouchableOpacity>

        {/* Scrollable content area with empty state */}
        <ScrollView
          style={mobileStyles.contentArea}
          contentContainerStyle={mobileStyles.contentAreaContainer}
          keyboardShouldPersistTaps="handled"
        >
          <ChatEmptyState type="startConversation" />
        </ScrollView>

        {/* Bottom section - Compose message (matches Figma) */}
        <View style={[mobileStyles.bottomSection, { paddingBottom: Math.max(insets.bottom, 24) }]}>
          {/* Subject input with floating label */}
          <View style={mobileStyles.inputContainer}>
            {subject.length > 0 && (
              <Text style={mobileStyles.floatingLabel}>Subject</Text>
            )}
            <TextInput
              style={[
                mobileStyles.input,
                subject.length > 0 && mobileStyles.inputWithLabel,
              ]}
              value={subject}
              onChangeText={setSubject}
              placeholder="Subject"
              placeholderTextColor={CHAT_MOBILE_COLORS.textPlaceholder}
              maxLength={100}
              returnKeyType="next"
            />
          </View>

          {/* Attachment preview */}
          {attachments.length > 0 && (
            <AttachmentPreview attachments={attachments as any} onRemove={removeAttachment} />
          )}

          {/* Message input row with attach icon and send button */}
          <View style={mobileStyles.messageRow}>
            <TouchableOpacity 
              style={mobileStyles.attachButton}
              onPress={() => showPhotoPickerActionSheet(addFromLibrary, addFromCamera)}
            >
              <AttachIcon width={24} height={24} />
            </TouchableOpacity>
            <View style={mobileStyles.messageInputContainer}>
              <TextInput
                style={mobileStyles.messageInput}
                value={messageContent}
                onChangeText={setMessageContent}
                placeholder="Your message..."
                placeholderTextColor={CHAT_MOBILE_COLORS.textMuted}
                multiline
              />
            </View>
            <TouchableOpacity
              style={mobileStyles.sendButton}
              onPress={handleSubmit}
              disabled={disableSend}
            >
              {disableSend ? (
                <SendIconInactive width={34} height={34} />
              ) : (
                <SendIconActive width={34} height={34} />
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </KeyboardSafeView>
  );
};

// Desktop styles
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  scroll: { flex: 1 },
  content: { padding: 16, paddingBottom: 24 },
  title: { fontSize: 18, fontWeight: '600', color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, marginBottom: 12 },
  fieldSection: { marginBottom: 12 },
  label: { fontSize: 14, color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY, marginBottom: 6 },
  input: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  compose: { marginTop: 12 },
  composeTitle: { fontSize: 16, color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, marginBottom: 8, fontWeight: '600' },
  composeInput: {
    minHeight: 140,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  footer: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: RESIDENTS_DESKTOP_COLORS.BORDER_SECONDARY,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  cancel: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  cancelText: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '600' },
  send: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#4773FE',
  },
  sendDisabled: { opacity: 0.6 },
  sendText: { color: '#FFFFFF', fontWeight: '700' },
});

export default ResidentCreateConversation;

// Mobile-specific styles matching Figma design
const mobileStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: CHAT_MOBILE_COLORS.cardBackground,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 20,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  contentArea: {
    flex: 1,
  },
  contentAreaContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  // Bottom section - Compose message per Figma
  bottomSection: {
    // paddingBottom is applied dynamically with safe area insets
    paddingHorizontal: 24,
    gap: 20,
  },
  // Input container with floating label support
  inputContainer: {
    backgroundColor: CHAT_MOBILE_COLORS.inputBackground, // rgba(238,241,244,0.05)
    borderRadius: 8,
    height: 40,
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  floatingLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: CHAT_MOBILE_COLORS.textPlaceholder, // #ACACAC
    lineHeight: 12,
    marginBottom: 2,
  },
  input: {
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_MOBILE_COLORS.textPrimary,
    lineHeight: 17.5,
    padding: 0,
  },
  inputWithLabel: {
    // When label is shown, adjust text positioning
  },
  // Message row with attach icon, input and send button
  messageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    borderTopWidth: 1,
    borderTopColor: CHAT_MOBILE_COLORS.divider, // #464646
    paddingTop: 20,
    marginHorizontal: -24,
    paddingHorizontal: 24,
  },
  messageInputContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 6,
  },
  messageInput: {
    fontSize: 14,
    fontWeight: '400',
    color: CHAT_MOBILE_COLORS.textPrimary,
    lineHeight: 21,
    padding: 0,
  },
  // Attach button
  attachButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Send button - SVG icons include 34x34 background with border radius
  sendButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
